import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import {
  BILLING_ADMIN_ROLE,
  PLATFORM_ROLES,
  type PlatformRole,
} from "@constructionautomation/backend/types/platformRoles";
import { EllipsisVertical, Loader2, Search, ShieldPlus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { useUserProfile } from "@/components/auth";
import { PageHeader } from "@/components/layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type RoleUserRow = {
  _id: Id<"users">;
  email: string;
  name: string | null;
  organizationName: string | null;
  orgRole: string;
  platformRoles: Array<{
    _id: Id<"platformRoleAssignments">;
    role: PlatformRole;
    createdAt: number;
  }>;
};

type SearchUserRow = {
  _id: Id<"users">;
  email: string;
  name: string | null;
  organizationName: string | null;
  orgRole: string;
  platformRoles: PlatformRole[];
};

const AVAILABLE_ROLES: PlatformRole[] = [...PLATFORM_ROLES];
const PAGE_SIZE = 10;

export const Route = createFileRoute("/admin/roles/")({
  component: AdminRolesPage,
});

function formatRole(role: string) {
  return role
    .split("_")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function matchesRoleUser(row: RoleUserRow, search: string) {
  const normalized = search.trim().toLowerCase();
  if (!normalized) {
    return true;
  }
  return [row.email, row.name ?? ""].some((value) => value.toLowerCase().includes(normalized));
}

function AdminRolesPage() {
  const { userProfile, isPlatformAdmin } = useUserProfile();
  const { data, isPending, error } = useQuery(
    convexQuery(api.admin.roles.listUsersWithPlatformRoles, {})
  );
  const grantPlatformRole = useMutation(api.admin.roles.grantPlatformRole);
  const changePlatformRole = useMutation(api.admin.roles.changePlatformRole);
  const revokePlatformRole = useMutation(api.admin.roles.revokePlatformRole);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [isGrantModalOpen, setIsGrantModalOpen] = useState(false);
  const [grantSearch, setGrantSearch] = useState("");
  const [debouncedGrantSearch, setDebouncedGrantSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<Id<"users"> | null>(null);
  const [selectedRole, setSelectedRole] = useState<PlatformRole>(BILLING_ADMIN_ROLE);
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedGrantSearch(grantSearch.trim());
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [grantSearch]);

  const rows = useMemo(() => (data as RoleUserRow[] | undefined) ?? [], [data]);
  const filteredRows = useMemo(
    () => rows.filter((row) => matchesRoleUser(row, search)),
    [rows, search]
  );
  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, page]);

  const { data: searchResultsData, isPending: isSearching } = useQuery(
    convexQuery(
      api.admin.roles.searchUsersForRoleGrant,
      debouncedGrantSearch.length >= 2 ? { query: debouncedGrantSearch } : "skip"
    )
  );
  const searchResults = (searchResultsData as SearchUserRow[] | undefined) ?? [];
  const selectedSearchUser =
    selectedUserId === null
      ? null
      : searchResults.find((row) => row._id === selectedUserId) ?? null;

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        Failed to load platform roles.
      </div>
    );
  }

  const handleGrant = async () => {
    if (!selectedUserId) {
      toast.error("Select a user to grant a role");
      return;
    }

    setPendingKey(`grant:${selectedUserId}:${selectedRole}`);
    try {
      await grantPlatformRole({ userId: selectedUserId, role: selectedRole });
      toast.success(`Granted ${formatRole(selectedRole)}`);
      handleGrantModalOpenChange(false);
    } catch (grantError) {
      toast.error(grantError instanceof Error ? grantError.message : "Failed to grant role");
    } finally {
      setPendingKey(null);
    }
  };

  const handleRevoke = async (userId: Id<"users">, role: PlatformRole) => {
    setPendingKey(`revoke:${userId}:${role}`);
    try {
      await revokePlatformRole({ userId, role });
      toast.success(`Revoked ${formatRole(role)}`);
    } catch (revokeError) {
      toast.error(revokeError instanceof Error ? revokeError.message : "Failed to revoke role");
    } finally {
      setPendingKey(null);
    }
  };

  const handleChangeRole = async (
    userId: Id<"users">,
    currentRole: PlatformRole,
    nextRole: PlatformRole
  ) => {
    if (currentRole === nextRole) {
      return;
    }

    setPendingKey(`change:${userId}:${currentRole}:${nextRole}`);
    try {
      await changePlatformRole({ userId, currentRole, nextRole });
      toast.success(
        `Changed ${formatRole(currentRole)} to ${formatRole(nextRole)}`
      );
    } catch (changeError) {
      toast.error(
        changeError instanceof Error ? changeError.message : "Failed to change role"
      );
    } finally {
      setPendingKey(null);
    }
  };

  const currentUserId = userProfile?.userId ?? null;
  const handleGrantModalOpenChange = (open: boolean) => {
    setIsGrantModalOpen(open);
    if (!open) {
      setGrantSearch("");
      setDebouncedGrantSearch("");
      setSelectedUserId(null);
      setSelectedRole(BILLING_ADMIN_ROLE);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Roles"
        description="Grant and revoke internal platform roles. Only platform admins can manage roles."
        breadcrumbs={[{ label: "Admin", href: "/admin" }, { label: "Roles" }]}
        actions={
          <Button
            onClick={() => setIsGrantModalOpen(true)}
            disabled={!isPlatformAdmin}
          >
            <ShieldPlus className="size-4" />
            Grant role
          </Button>
        }
      />

      <Card className="py-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Platform Role Assignments</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-sm">
              <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                className="pl-9"
                placeholder="Search by name or email"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Showing {filteredRows.length} user{filteredRows.length === 1 ? "" : "s"} with platform roles.
            </p>
          </div>

          <div className="rounded-lg border-2 border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-bold uppercase tracking-wider text-xs">User</TableHead>
                  <TableHead className="font-bold uppercase tracking-wider text-xs">Organization</TableHead>
                  <TableHead className="font-bold uppercase tracking-wider text-xs">Org Role</TableHead>
                  <TableHead className="font-bold uppercase tracking-wider text-xs">Platform Roles</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                      No users with platform roles match this search.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map((row) => {
                    const isSelf = currentUserId === row._id;
                    return (
                      <TableRow key={row._id}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-foreground">{row.name ?? row.email}</p>
                            <p className="text-xs text-muted-foreground">{row.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{row.organizationName ?? "-"}</TableCell>
                        <TableCell>{row.orgRole}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            {row.platformRoles.map((assignment) => (
                              <div key={assignment._id} className="flex items-center gap-1">
                                <Badge variant="outline">
                                  {formatRole(assignment.role)}
                                </Badge>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon-sm"
                                      disabled={pendingKey !== null || isSelf}
                                      title={isSelf ? "You cannot change your own roles" : undefined}
                                    >
                                      {pendingKey?.startsWith(`revoke:${row._id}:${assignment.role}`) ||
                                      pendingKey?.startsWith(
                                        `change:${row._id}:${assignment.role}:`
                                      ) ? (
                                        <Loader2 className="size-3 animate-spin" />
                                      ) : (
                                        <EllipsisVertical className="size-4" />
                                      )}
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="start">
                                    <DropdownMenuLabel>
                                      {formatRole(assignment.role)}
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {AVAILABLE_ROLES.filter((role) => role !== assignment.role).map(
                                      (role) => (
                                        <DropdownMenuItem
                                          key={`${assignment._id}:${role}`}
                                          onClick={() =>
                                            void handleChangeRole(row._id, assignment.role, role)
                                          }
                                        >
                                          Change to {formatRole(role)}
                                        </DropdownMenuItem>
                                      )
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      variant="destructive"
                                      onClick={() =>
                                        void handleRevoke(row._id, assignment.role)
                                      }
                                    >
                                      Remove role
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <p>
              Page {page} of {pageCount}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pageCount}
                onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isGrantModalOpen} onOpenChange={handleGrantModalOpenChange}>
        <DialogContent className="!h-auto !w-[95vw] !max-w-[95vw] md:!w-[60vw] md:!max-w-[60vw] max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Grant Platform Role</DialogTitle>
            <DialogDescription>
              Search for a user by name or email, then choose the role to grant.
            </DialogDescription>
          </DialogHeader>

          <div className="flex min-h-0 flex-col gap-4 overflow-hidden">
            <div className="grid gap-4 sm:grid-cols-[1fr_220px]">
              <div className="relative">
                <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={grantSearch}
                  onChange={(event) => setGrantSearch(event.target.value)}
                  className="pl-9"
                  placeholder="Search by name or email"
                />
              </div>
              <Select value={selectedRole} onValueChange={(value) => setSelectedRole(value as PlatformRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AVAILABLE_ROLES.map((role) => (
                    <SelectItem key={role} value={role}>
                      {formatRole(role)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-h-0 overflow-auto rounded-lg border-2 border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead>Current Roles</TableHead>
                    <TableHead className="text-right">Select</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {debouncedGrantSearch.length < 2 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                        Enter at least 2 characters to search.
                      </TableCell>
                    </TableRow>
                  ) : isSearching ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="size-4 animate-spin" />
                          Searching users...
                        </span>
                      </TableCell>
                    </TableRow>
                  ) : searchResults.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                        No matching users.
                      </TableCell>
                    </TableRow>
                  ) : (
                    searchResults.map((row) => {
                      const isSelf = currentUserId === row._id;
                      return (
                        <TableRow key={row._id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-foreground">{row.name ?? row.email}</p>
                              <p className="text-xs text-muted-foreground">{row.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>{row.organizationName ?? "-"}</TableCell>
                          <TableCell>
                            {row.platformRoles.length === 0 ? (
                              <span className="text-sm text-muted-foreground">None</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {row.platformRoles.map((role) => (
                                  <Badge key={`${row._id}:${role}`} variant="outline">
                                    {formatRole(role)}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant={selectedUserId === row._id ? "default" : "outline"}
                              size="sm"
                              disabled={isSelf}
                              onClick={() => setSelectedUserId(row._id)}
                              title={isSelf ? "You cannot change your own roles" : undefined}
                            >
                              {selectedUserId === row._id ? "Selected" : "Select"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <DialogFooter showCloseButton>
            <Button onClick={() => void handleGrant()} disabled={pendingKey !== null || selectedSearchUser === null}>
              {pendingKey?.startsWith("grant:") ? <Loader2 className="size-4 animate-spin" /> : null}
              Grant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
